import { c as _c } from "react/compiler-runtime";
import { TableFooter as KuramaTableFooter, TableRow } from '@precis-digital/kurama/Table';
import { styled } from '@precis-digital/kurama/utils';
import type { ColumnPinningState } from '@tanstack/react-table';
import { forwardRef } from 'react';
import type { ForwardedRef, ReactElement, ReactNode } from 'react';
import type { ElementsDescriptionsMap, TotalsAndRangeData } from '../../../internalTypes';
import type { ChartCardDefaults, ChartCardTFunction, ChartDataRow } from '../../../types';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import TableFooterRow from './TableFooterRow';
import { Z_INDEX } from '../constants';
import type { ChartCardTable } from '../tanstack';
interface TableFooterProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  children?: ReactNode;
  table: ChartCardTable<DataKey, DataRow>;
  columnPinning: ColumnPinningState;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  allowColumnResizing: boolean;
  totalsData: TotalsAndRangeData<DataKey>['totals'];
  /** When true, footer cells display `totalsData` instead of aggregating the current page. */
  preferTotalsData?: boolean;
  t: ChartCardTFunction;
}
const TableFooterInner = (t0, ref) => {
  const $ = _c(22);
  const {
    table: t1,
    columnPinning,
    elementsDescriptionsMap,
    allowColumnResizing,
    totalsData,
    preferTotalsData: t2,
    t,
    children
  } = t0;
  const {
    getFooterGroups
  } = t1;
  const preferTotalsData = t2 === undefined ? false : t2;
  const defaults = useChartCardDefaults();
  let t3;
  if ($[0] !== allowColumnResizing || $[1] !== columnPinning || $[2] !== elementsDescriptionsMap || $[3] !== getFooterGroups || $[4] !== preferTotalsData || $[5] !== t || $[6] !== totalsData) {
    let t4;
    if ($[8] !== allowColumnResizing || $[9] !== columnPinning || $[10] !== elementsDescriptionsMap || $[11] !== preferTotalsData || $[12] !== t || $[13] !== totalsData) {
      t4 = footerGroup => <TableFooterRow key={footerGroup.id} footerGroup={footerGroup} columnPinning={columnPinning} elementsDescriptionsMap={elementsDescriptionsMap} allowColumnResizing={allowColumnResizing} totalsData={totalsData} preferTotalsData={preferTotalsData} t={t} />;
      $[8] = allowColumnResizing;
      $[9] = columnPinning;
      $[10] = elementsDescriptionsMap;
      $[11] = preferTotalsData;
      $[12] = t;
      $[13] = totalsData;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = getFooterGroups().map(t4);
    $[0] = allowColumnResizing;
    $[1] = columnPinning;
    $[2] = elementsDescriptionsMap;
    $[3] = getFooterGroups;
    $[4] = preferTotalsData;
    $[5] = t;
    $[6] = totalsData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[15] !== children) {
    t4 = children != null && children !== false ? <StyledPaginationRow><StyledPaginationCell>{children}</StyledPaginationCell></StyledPaginationRow> : null;
    $[15] = children;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== defaults || $[18] !== ref || $[19] !== t3 || $[20] !== t4) {
    t5 = <StyledTableFooter ref={ref} defaults={defaults}>{t3}{t4}</StyledTableFooter>;
    $[17] = defaults;
    $[18] = ref;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
const StyledTableFooter = styled(KuramaTableFooter, {
  shouldForwardProp: prop => prop !== 'defaults'
})<{
  defaults: ChartCardDefaults;
}>(({
  defaults
}) => ({
  display: 'grid',
  order: 3,
  position: 'sticky',
  bottom: 0,
  zIndex: Z_INDEX.footer,
  backgroundColor: defaults.table.backgroundColor.footer
}));

/** Pagination renders as a div; tfoot may only contain tr, and tr may only contain td/th. */
const StyledPaginationRow = styled(TableRow)({
  display: 'block',
  width: '100%'
});
const StyledPaginationCell = styled('td')({
  display: 'block',
  width: '100%',
  padding: 0,
  border: 'none'
});
const TableFooter = forwardRef(TableFooterInner) as <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: TableFooterProps<DataKey, DataRow> & {
  ref?: ForwardedRef<HTMLTableSectionElement>;
}) => ReactElement;
export default TableFooter;